import { Card, CardContent, Chip, Stack, Typography } from "@mui/material";
import { TrendingDown, TrendingFlat, TrendingUp } from "@mui/icons-material";
import { useAppTheme } from "../../context/ThemeContext";

type ComponentProps = {
  title: string;
  value: number;
  comment: string;
  percentage: number;
  accentColor?: string;
  prefix?: string;        // e.g. "RM" for currency values
  isCurrency?: boolean;   // show 2 decimal places when true
};

function AnalyticCard({
  title,
  value,
  percentage,
  accentColor = "#0F172A",
  prefix,
  isCurrency = false,
}: ComponentProps) {
  const { isNewTheme } = useAppTheme();

  const getTrendIcon = (pct: number) => {
    if (pct === 0) return <TrendingFlat fontSize="small" />;
    if (pct > 0) return <TrendingUp fontSize="small" />;
    return <TrendingDown fontSize="small" />;
  };

  const getTrendColor = (pct: number): "default" | "success" | "error" => {
    if (pct === 0) return "default";
    if (pct > 0) return "success";
    return "error";
  };

  const formattedValue = (() => {
    const num = isCurrency
      ? new Intl.NumberFormat("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
      : Intl.NumberFormat().format(value);
    return prefix ? `${prefix} ${num}` : num;
  })();

  return (
    <Card
      className={isNewTheme ? "card-bounce-in" : undefined}
      sx={
        isNewTheme
          ? {
              maxWidth: "100%",
              width: "100%",
              borderRadius: "8px",
              border: "1px solid #E2E8F0",
              borderTop: `3px solid ${accentColor}`,
              boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
              transition: "box-shadow 0.15s ease, transform 0.15s ease",
              "&:hover": {
                boxShadow: "0 4px 16px rgba(15,23,42,0.07)",
                transform: "translateY(-1px)",
              },
            }
          : {
              maxWidth: "100%",
              width: "100%",
              borderRadius: "10px",
              boxShadow: "0px 0px 55px 5px rgba(0, 0, 0, 0.05)",
            }
      }>
      <CardContent sx={{ p: isNewTheme ? 2.5 : 2 }}>
        <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
          {title}
        </Typography>

        <Stack direction="column" spacing={isNewTheme ? 1.5 : 2} alignItems="flex-start" mt={isNewTheme ? 1 : 0}>
          <Typography
            variant={isNewTheme ? "h4" : "h5"}
            component="div"
            fontWeight={isNewTheme ? 700 : "semibold"}
            sx={{ lineHeight: 1, fontSize: isCurrency && isNewTheme ? "1.5rem !important" : undefined }}>
            {formattedValue}
          </Typography>

          <Chip
            label={`${percentage.toPrecision(4)}%`}
            icon={getTrendIcon(percentage)}
            size="small"
            color={getTrendColor(percentage)}
            sx={{ borderRadius: isNewTheme ? "4px" : "5px", fontWeight: isNewTheme ? 600 : 400 }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

export default AnalyticCard;
